import { clsx } from "clsx";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import pluralize from "pluralize";

export function cn(...inputs) {
	return twMerge(clsx(inputs));
}

export const getFromLocalStorage = (key, defaultValue = undefined) => {
	try {
		const value = localStorage.getItem(key);
		return value ? JSON.parse(value) : defaultValue;
	} catch (error) {
		console.error(error);
		return undefined;
	}
};

export const setToLocalStorage = (key, value) => {
	try {
		localStorage.setItem(key, JSON.stringify(value));
	} catch (error) {
		console.error(`Error saving "${key}":`, error);
	}
};

export const removeFromLocalStorage = (...keys) => keys.forEach((key) => localStorage.removeItem(key));

export const clearLocalStorage = () => {
	try {
		localStorage.clear();
	} catch (error) {
		console.error(`Error clearing local storage:`, error);
	}
};

export const showSuccessToast = (message, data) =>
	toast.success(message, {
		// icon: <CheckCircleFillIcon className="text-green-500 size-5" />,
		...data,
	});

export const showErrorToast = (message, data) =>
	toast.error(message, {
		// icon: <XCircleFillIcon className="text-red-500 size-5" />,
		...data,
	});

export const showInfoToast = (message, data) =>
	toast.info(message, {
		// icon: <InfoCircleFillIcon className="text-blue-500 size-5" />,
		...data,
	});

export const showLoadingToast = (message, data) => toast.loading(message, data);

export const showWarningToast = (message, data) =>
	toast.warning(message, {
		// icon: <AlertCircleFillIcon className="text-yellow-500 size-5" />,
		...data,
	});

export const showPromiseToast = (promise, { loading, success, error, ...rest }) =>
	toast.promise(promise, {
		loading,
		success,
		error,
		duration: 2000,
		...rest,
	});

export function removeEmptyOrNull(obj) {
	if (!obj) return undefined;

	const filteredEntries = Object.entries(obj).filter(([_, value]) => value !== "" && value !== null);

	return filteredEntries.length > 0 ? Object.fromEntries(filteredEntries) : {};
}

export const renderCurrency = (amount = 0, currency = "NGN") =>
	new Intl.NumberFormat("en-GB", {
		style: "currency",
		currency,
		currencyDisplay: "narrowSymbol",
		minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
		maximumFractionDigits: 2,
	}).format(parseFloat(amount.toString()) || 0);

export const renderNumber = (amount = 0) =>
	new Intl.NumberFormat("en-GB", {
		currencyDisplay: "narrowSymbol",
		minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
		maximumFractionDigits: 2,
	}).format(parseFloat(amount.toString()) || 0);

export const isSpanElement = (element) => element instanceof HTMLSpanElement;

export const copyToClipboard = async (text) => {
	if ("clipboard" in navigator) {
		return Promise.resolve(navigator.clipboard.writeText(text));
	} else {
		return Promise.resolve(document.execCommand("copy", true, text));
	}
};

function capitalizeFirstLetter(string) {
	if (string.length === 0) {
		return ""; // Handle empty strings gracefully
	}
	return string.charAt(0).toUpperCase() + string.slice(1);
}

export const getCustomizationValue = (user, label, returnSingular = false) => {
	const value = user?.business_customization?.find(({ label: customizationLabel }) => customizationLabel === label)?.value;

	if (returnSingular) return capitalizeFirstLetter(value);

	return capitalizeFirstLetter(pluralize.plural(value));
};

export function toChartSeries(monthlyData) {
	return [
		{
			label: "Invoices",
			data: monthlyData.map((item) => ({
				primary: item.month,
				secondary: item.invoices,
			})),
		},
		{
			label: "Payments",
			data: monthlyData.map((item) => ({
				primary: item.month,
				secondary: item.payments,
			})),
		},
	];
}
